export abstract class SimpleChatModel {
  public abstract doGenerate(data: BaseChatModelInput, options?: ReqOptions): Promise<DoGenerateOutput>

  public abstract doStream(data: BaseChatModelInput, options?: ReqOptions): Promise<DoStreamOutput>
}

type RawResponse = { rawResponse?: any }

export type DoGenerateOutput = BaseDoGenerateOutput & RawResponse

export type DoStreamOutput = AsyncIterableReadableStream<BaseDoStreamOutputChunk & RawResponse>

export type ChatModelConstructor = typeof SimpleChatModel

export type AsyncIterableReadableStream<T> = ReadableStream<T> & {
  [Symbol.asyncIterator]: () => { next(): Promise<IteratorResult<T>> }
}

export interface IModelReqInput {
  url: string
  headers?: Record<string, string>
  data?: Object
  stream?: boolean
  timeout?: number
}

export type ModelReq = <T extends IModelReqInput>(
  props: T,
) => T['stream'] extends true ? Promise<ReadableStream<Uint8Array>> : Promise<Object>

export interface IBotReqInput {
  url: string
  method: string
  headers?: Record<string, string>
  data?: Object
  stream?: boolean
  timeout?: number
}

export interface ReqOptions {
  timeout?: number
}

export type BotReq = <T extends IBotReqInput>(
  props: T,
) => T['stream'] extends true ? Promise<ReadableStream<Uint8Array>> : Promise<Object>

export type UserMessage = {
  role: 'user'
  content: string
}

export type SystemMessage = {
  role: 'system'
  content: string
}

export type AssistantMessage = PlainAssistantMessage | ToolCallAssistantMessage

export type PlainAssistantMessage = {
  role: 'assistant'
  content: string
}

export type ToolCallAssistantMessage = {
  role: 'assistant'
  tool_calls: Array<ToolCall>
  content?: string
}

export type ToolMessage = {
  role: 'tool'
  tool_call_id: string
  content: string
}

export type ChatModelMessage = UserMessage | SystemMessage | AssistantMessage | ToolMessage

export type FunctionTool = {
  name: string
  description: string
  fn: CallableFunction
  parameters: object
}

// #region 大模型标准输入类型
export interface BaseChatModelInput {
  model: string
  messages: Array<ChatModelMessage>
  temperature?: number
  top_p?: number
  tools?: Array<ModelTool>
  tool_choice?: 'none' | 'auto' | 'custom'
}

/**
 * 给 LLM 发请求时要传的工具
 */
export type ModelTool = {
  type: string
  function: ModelToolFunction
}

/**
 * 给 LLM 发请求时要传的工具函数
 */
export type ModelToolFunction = {
  name: string
  description: string
  /**
   * 描述函数参数的 JSON Schema
   */
  parameters: object
}
// #endregion

// #region 大模型标准响应类型
export type ToolCall = {
  id: string
  type: string
  function: { name: string; arguments: string }
}

type FinishReason = 'tool_calls' | (string & {})

export type Usage = {
  completion_tokens: number
  prompt_tokens: number
  total_tokens: number
}

export interface BaseDoGenerateOutput {
  choices?: Array<{
    finish_reason?: FinishReason
    message?: ChatModelMessage
  }>
  usage?: Usage
}

export interface BaseDoStreamOutputChunk {
  choices?: Array<{
    finish_reason?: FinishReason
    delta?: ChatModelMessage
  }>
  usage?: Usage
}
// #endregion

// #region Image Model Types
/**
 * 图片生成输入参数
 */
export interface ImageGenerateInput {
  model: string // 模型名称，例如 'hunyuan-image'、'dall-e-3'
  prompt: string // 用于描述要生成的图片内容
  size?: string // 尺寸
  n?: number // 张数
  [key: string]: unknown // 允许额外的厂商特定参数
}

/**
 * 图片编辑输入参数
 */
export interface ImageEditInput {
  model: string
  prompt: string
  size?: string // 尺寸
  n?: number // 张数
  [key: string]: unknown // 允许额外的厂商特定参数
}

export interface HunyuanGenerateImageInput {
  model: 'hunyuan-image'
  /** 用来生成图像的文本描述 */
  prompt: string
  /**
   * 模型版本，支持 v1.8.1 和 v1.9，默认版本 v1.8.1
   * - v1.8.1: 采用二阶段模型支持文生图任务，针对图像细节、稳定性等要求高的任务较为合适
   * - v1.9: 采用模型矩阵能力支持文生图任务，后端由人像、游戏、通用模型，以及各类功能插件组成，灵活适配多种文生图任务
   */
  version?: 'v1.8.1' | 'v1.9' | (string & {})
  /**
   * 图片尺寸，默认 "1024x1024"
   * v1.9 仅支持以下几个尺寸：
   * - 1024x1024
   * - 1024x768, 1152x864
   * - 768x1024, 864x1152
   * - 1280x768 (16:9)
   * - 768x1280 (9:16)
   * - 1280x720 (16:9)
   * - 720x1280 (9:16)
   *
   * 支持用户自定义尺寸，但输入尺寸只支持 768-1280 之间且为 64 的倍数的数字
   */
  size?: string
  /**
   * 仅 v1.9 支持，负向词
   * 如果不传则使用各个模型内部的默认负向词
   */
  negative_prompt?: string
  /**
   * 仅 v1.9 支持，可指定风格：
   * - 古风二次元风格
   * - 都市二次元风格
   * - 悬疑风格
   * - 校园风格
   * - 都市异能风格
   */
  style?: '古风二次元风格' | '都市二次元风格' | '悬疑风格' | '校园风格' | '都市异能风格' | (string & {})
  /**
   * 为 true 时对 prompt 进行改写，实际生成的图片会使用改写后的 prompt 进行生成
   * 多数场景可提升生成的图片效果，默认为 true
   */
  revise?: boolean
  /**
   * 生成图片个数，默认为 1
   * 目前只支持生成一张，固定为 1，传其他的值会报错
   */
  n?: number
  /**
   * 业务自定义水印内容
   * 限制 16 个字符长度（不区分中英文），生成在图片右下角
   */
  footnote?: string
  /**
   * 生成种子，仅当生成图片数为 1 时生效
   * 范围 [1, 4294967295]，不传时默认随机
   */
  seed?: number
}

export interface HunyuanARGenerateImageInput {
  /** 模型名称：hunyuan-image-v3.0-v1.0.4（推荐）或 hunyuan-image-v3.0-v1.0.1 */
  model:
  | 'HY-Image-3.0-Plus-4090-Tob-v1.0'
  | 'HY-Image-v3.0-I2I-ToB-v1.0.1'
  | 'hunyuan-image-v3.0-v1.0.4'
  | 'hunyuan-image-v3.0-v1.0.1'
  | 'hunyuan-image-v3.0-v1.0.5'
  /** 生成图片使用的文本，不超过 8192 字符 */
  prompt: string
  /**
   * 图片尺寸，格式 "${宽}x${高}"，默认 "1024x1024"
   * HY-Image-3.0-Plus-4090-Tob-v1.0 hunyuan-image-v3.0-v1.0.4：宽高 [512, 2048]，面积不超过 1024x1024
   * hunyuan-image-v3.0-v1.0.1：支持固定尺寸列表
   */
  size?: string
  /** 生成种子，仅当生成图片数为 1 时生效，范围 [1, 4294967295] */
  seed?: number
  /** 业务自定义水印内容，限制 16 字符，生成在图片右下角 */
  footnote?: string
  /** 是否对 prompt 改写，默认开启。改写会增加约 30s 耗时 */
  revise?: { value: boolean }
  /** 改写是否开启 thinking 模式，默认开启。开启后效果提升但耗时增加（最大 60s） */
  enable_thinking?: { value: boolean }
  /**
   * 垫图url列表，与image二选一，优先选image，大小不超过10MB，支持 jpg jpeg png 格式，最多3张图，image_urls和images必有一个非空
   */
  image_urls?: string[]
  /**
   * 垫图base64列表，与image_url二选一，优先选image，大小不超过10MB，支持 jpg jpeg png 格式，最多3张图
   */
  images?: string[]
}

export interface HunyuanARGenerateImageOutput {
  /** 此次请求的 id */
  id: string
  /** unix 时间戳 */
  created: number
  /** 返回的图片生成内容 */
  data: Array<{
    /** 生成的图片 url，有效期 24 小时 */
    url: string
    /** 改写后的 prompt */
    revised_prompt?: string
  }>
}

export type HunyuanEditImageInput = {
  /** 模型名称，本期为 hunyuan-image，即为商品图编辑场景 */
  model: 'hunyuan-image' | (string & {})
  /** 编辑的文本描述 */
  prompt: string
  /** 模型版本 */
  version?: string
  /**
   * 图片尺寸，默认 "800x800"
   * 支持用户自定义尺寸，但输入尺寸只支持 512-1280 之间
   * 目前只支持 1:1、16:9、9:16 三个尺寸
   */
  size?: string
  /**
   * 生成图片个数，默认为 1
   * 目前只支持生成一张，固定为 1，传其他的值会报错
   */
  n?: number
  /**
   * beta 参数，随机种子
   * 范围 [1, 4294967295]，不传时默认随机
   */
  seed?: number
  /**
   * 业务自定义角标内容
   * 限制 16 个字符长度（不区分中英文）
   */
  footnote?: string
  /**
   * 图 base64 编码，大小不超过 6M
   * 支持 jpg/jpeg 格式，和 image_url 二选一
   * 长短边比例要求 2:1 内
   */
  image?: string
  /**
   * 图片 url
   * 长短边比例要求 2:1 内，和 image 二选一
   */
  image_url?: string
} & (
  | {
    mask: string
    mask_url?: never
  }
  | {
    mask?: never
    mask_url: string
  }
)

/**
 * 混元图片生成输出
 */
export interface HunyuanGenerateImageOutput {
  /** 此次请求的 id */
  id: string
  /** unix 时间戳 */
  created: number
  /** 返回的图片生成内容 */
  data: Array<{
    /** 生成的图片 url，有效期为 24 小时 */
    url: string
    /** 原 prompt 改写后的文本。若 revise 为 false，则为原 prompt */
    revised_prompt?: string
  }>
}

/**
 * 混元图片编辑输出
 */
export interface HunyuanEditImageOutput {
  /** 此次请求的 id */
  id: string
  /** unix 时间戳 */
  created: number
  /** 返回的图片生成内容 */
  data: Array<{
    /** 生成的图片 url，有效期为 24 小时 */
    url: string
  }>
}

// #endregion
